import * as React from 'react'
import { Alert, Button, Form, Spinner } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import contactAPI from 'api/contact'
import { ContactCategory } from 'api/common/constants'
import Footer from 'components/Footer'
import Header from 'components/Header'
import InputField from 'components/InputField'
import InputSelectField from 'components/InputSelectField'
import InputTextAreaField from 'components/InputTextAreaField'
import { scrollToAnchor } from 'utilities/helper'
import { contactUsFormItems } from 'utilities/resources/formItems'
import { ContactUsFormValues } from 'utilities/resources/formItemValues'
import { consultationTypes, KeyValue } from 'utilities/resources/options'
import loginData from 'utilities/loginData'
import { emailValidator, emptyValidator, ErrorChecker, FormErrorMessage, selectValidator, telValidator } from 'utilities/validators'
import { PropertySetter, autoCast } from 'utilities/typeUtility'
import myPageAPI from 'api/myPage'
import { Status } from 'api/common/statuses'
import { connectorGetAPI } from 'api/connector'
import { MyPageResponse } from 'api/common/responses'

/**
 * お問い合わせ
 * @returns
 */
const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const campaign = queryParams.get('campaign')
  const [information, setInformation] = React.useState('')
  const [committed, setCommitted] = React.useState(false)
  const [formValues, setFormValues] = React.useState<ContactUsFormValues>({
    connectorId: '',
    name: '',
    tel: '',
    email: '',
    category: '',
    body: '',
  })
  const setter = new PropertySetter(setFormValues)
  const [errorValues, setErrorValues] = React.useState<FormErrorMessage<ContactUsFormValues>>()
  const errorChecker = new ErrorChecker<ContactUsFormValues>(contactUsFormItems)

  const consultationCategory: KeyValue[] = campaign
    ? consultationTypes.filter((item) => item.key === String(ContactCategory.Campaign))
    : consultationTypes.filter((item) => item.key !== String(ContactCategory.Campaign))

  /**
   * お問い合わせフォームに必要なコネクター情報を取得
   */
  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      setFormValues({
        connectorId: '',
        name: '',
        tel: '',
        email: '',
        category: '',
        body: '',
      })
      return
    }

    async function fetchData() {
      const myPageResult = await new Promise<MyPageResponse>((resolve) => {
        myPageAPI((status, result) => {
          if (status === Status.Success) resolve(result)
          else setInformation('コネクター情報の取得に失敗しました')
        })
      })
      const connectorResult = await new Promise<{ formValues: { phoneNumber: string } }>((resolve) => {
        connectorGetAPI((status, result) => {
          if (status === Status.Success) resolve(result)
          else setInformation('コネクター情報の取得に失敗しました')
        })
      })
      setFormValues({
        connectorId: String(myPageResult.mainPositionId),
        name: myPageResult.connectorName,
        tel: connectorResult.formValues.phoneNumber,
        email: myPageResult.connectorEmail,
        category: campaign ? String(ContactCategory.Campaign) : '',
        body: '',
      })
    }
    fetchData()
  }, [campaign])

  /**
   * 「送信する」ボタン
   */
  const handleSubmit = () => {
    errorChecker.init()
    errorChecker.check(emptyValidator(formValues?.name), 'name')
    errorChecker.check(telValidator(formValues?.tel), 'tel')
    errorChecker.check(emailValidator(formValues?.email), 'email')
    errorChecker.check(selectValidator(formValues?.category, ContactCategory), 'category')
    errorChecker.check(emptyValidator(formValues?.body), 'body')
    setErrorValues(errorChecker.getErrorMessageData())
    if (errorChecker.isValid()) {
      setCommitted(true)
      contactAPI(
        {
          connectorId: formValues!.connectorId,
          name: formValues!.name,
          tel: formValues!.tel,
          email: formValues!.email,
          category: autoCast(formValues!.category),
          body: formValues!.body,
        },
        (result: boolean) => {
          if (result) {
            navigate('/contactUsComplete', { state: { email: formValues!.email } })
          } else {
            setCommitted(false)
            setInformation('お問い合わせの受付に失敗しました。')
          }
        },
      )
    } else {
      scrollToAnchor(errorChecker.getFirstInvalidItemId())
    }
  }
  return (
    <>
      <Header title="お問い合わせ" />
      <main>
        <section className="mx-4 my-5">
          <p>
            調査の必要なお問い合わせにつきましては、回答にお時間がかかる場合がございます。
            <br />
            平日営業時間内の回答とさせていただきます。
          </p>
          <Form>
            {loginData.isLoggedIn() ? (
              <>
                <div className="fw-bold mt-3">コネクターID</div>
                <div>{formValues.connectorId}</div>
                <div className="fw-bold mt-3">コネクター名</div>
                <div>{formValues.name}</div>
              </>
            ) : (
              <>
                <InputField
                  name={contactUsFormItems.connectorId.name}
                  value={formValues?.connectorId}
                  labelTop={contactUsFormItems.connectorId.label}
                  type="text"
                  bold
                  className="mt-3"
                  placeholder="コネクターIDを入力してください"
                  onChange={(e) => setter.set('connectorId', e)}
                  errors={[errorValues?.connectorId ?? '']}
                />
                <InputField
                  name={contactUsFormItems.name.name}
                  value={formValues?.name}
                  labelTop={contactUsFormItems.name.label}
                  type="text"
                  bold
                  className="mt-3"
                  placeholder="名前を入力してください"
                  onChange={(e) => setter.set('name', e)}
                  errors={[errorValues?.name ?? '']}
                />
              </>
            )}
            <InputField
              name={contactUsFormItems.tel.name}
              value={formValues?.tel}
              labelTop={contactUsFormItems.tel.label}
              type="tel"
              bold
              className="mt-3"
              placeholder="電話番号を入力してください"
              onChange={(e) => setter.set('tel', e)}
              errors={[errorValues?.tel ?? '']}
            />
            <InputField
              name={contactUsFormItems.email.name}
              value={formValues?.email}
              labelTop={contactUsFormItems.email.label}
              type="email"
              bold
              className="mt-3"
              placeholder="メールアドレスを入力してください"
              onChange={(e) => setter.set('email', e)}
              errors={[errorValues?.email ?? '']}
            />
            <InputSelectField
              name={contactUsFormItems.category.name}
              value={formValues?.category}
              label={contactUsFormItems.category.label}
              items={consultationCategory}
              bold
              className="mt-3"
              onChange={(e) => setter.set('category', e)}
              errors={[errorValues?.category ?? '']}
            />
            <InputTextAreaField
              name={contactUsFormItems.body.name}
              value={formValues?.body}
              label={contactUsFormItems.body.label}
              placeholder={
                campaign ? '新名称を予想して入力してください。\n1名義につき1回のみご応募可能です。\n（複数回答はすべて無効となるのでご注意ください）' : '相談内容を入力してください'
              }
              bold
              className="mt-3"
              onChange={(e) => setter.set('body', e)}
              errors={[errorValues?.body ?? '']}
              rows={campaign ? 4 : undefined}
            />
            {information && (
              <Alert variant="danger" className="note text-justify border-0 mt-5">
                {information}
              </Alert>
            )}
            <div className="mt-5">
              <Button variant="primary" className="w-100 py-4" onClick={handleSubmit} disabled={committed}>
                <Spinner className="me-2" as="span" animation="border" size="sm" role="status" aria-hidden="true" hidden={!committed} />
                送信する
              </Button>
            </div>
          </Form>
          <p className="note mt-5">
            ※お問い合わせフォームにご入力いただいたメールアドレス宛にお返事いたします。
            <br />
            お客様のご利用環境、または迷惑メール対策等の設定により、お返事が届かない場合があります。
            <br />
            必ず、「info@e-commus.co.jp」からのメール受信が可能な設定にしていただきますようお願いいたします。
          </p>
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
